<script setup lang="ts">
import draggable from 'vuedraggable'
import { startOfWeek, addDays } from 'date-fns'
import type MDBSemi from '@/types/ferme/semi'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, to_wd, to_dd_MM, to_dd_MMMM, emptyMongoId, getSemiVérifiéColor } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'



const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isLoading = ref(false)
const newData = ref(false)

const selectedWeek = ref(new Date(storeU.journéeSélectionnée))
const weekStart = computed(() => startOfWeek(selectedWeek.value ?? new Date(), { weekStartsOn: 1 }))
const weekDays = computed<Date[]>(() =>
  Array.from({ length: 7 }, (_, i) => addDays(weekStart.value, i))
)

const buckets = ref([] as MDBVegcoBucket[])
const commandeBuckets = ref([] as MDBVegcoBucket[])
const cultures = ref([] as string[])
const semis = ref<MDBSemi[]>([])
const semisVegco = ref<MDBSemi[]>([])

const selectedCulture = ref('')

const sites = storeU.utilisateur.pagesListeNoire?.includes('vgco.tous')
  ? [storeU.siteSélectionné.lettresDappel]
  : ['Tous', 'DLFL', 'FHVW', 'PHVW', 'JPLG']
const selectedSite = ref(storeU.siteSélectionné.lettresDappel)

watch(weekStart, () => getData())
watch(selectedSite, () => getData())
watch(selectedCulture, () => {})

async function getData() {
  try {
    isLoading.value = true
    const wStart = weekStart.value
    const wEnd = addDays(wStart, 6)
    wEnd.setHours(23, 59, 59, 999)

    cultures.value = []
    for (const culture of ['Iceberg / Pommée', 'Laitue Romaine',
      'Laitue Feuille Verte', 'Laitue Feuille Rouge', 'Bok Choy', 'Nappa']) {
      if (storeU.cultures.find(c => c.nom == culture)) {
        cultures.value.push(culture)
      }
    }

    if (!selectedCulture.value && cultures.value.length > 0) {
      selectedCulture.value = cultures.value[0]
    }

    let siteCS = selectedSite.value == 'Tous' ? 'VGCO' : selectedSite.value
    if (siteCS == 'VGCO') siteCS = 'Tous'
    const effectiveSite = selectedSite.value == 'Tous' ? 'VGCO' : selectedSite.value
    const siteId = await storeU.getOrFetchSiteId(effectiveSite)

    await storeVegco.loadDaysMultipleCultures(wStart, wEnd, cultures.value)

    const bucketsList = storeVegco.bucketsStore.filter(bucket => {
      const d = new Date(bucket.date)
      return d >= wStart && d <= wEnd &&
        (bucket.siteId == siteId || bucket.siteGroupeId == siteId)
    })

    // Load semis
    const currentSiteId = storeU.siteSélectionné.id
    const newSemiIds: string[] = []
    for (const b of bucketsList) {
      if (b.siteId == currentSiteId && b.semiId != emptyMongoId() &&
        !semis.value.find(s => s.id == b.semiId)) {
        newSemiIds.push(b.semiId)
      }
    }
    if (newSemiIds.length > 0) {
      const siteLetters = storeU.siteSélectionné.lettresDappel
      const result = await apiProphet.post(`sites/${siteLetters}/semis/multiple`, newSemiIds)
      semis.value.push(...result.data as MDBSemi[])
    }

    const newSemiIdsVegco: string[] = []
    for (const b of bucketsList) {
      if (b.siteId != currentSiteId && b.semiId != emptyMongoId() &&
        !semisVegco.value.find(s => s.id == b.semiId)) {
        newSemiIdsVegco.push(b.semiId)
      }
    }
    if (newSemiIdsVegco.length > 0) {
      const result = await apiProphet.post(`vgco/semis/multiple`, newSemiIdsVegco)
      semisVegco.value.push(...result.data as MDBSemi[])
    }

    commandeBuckets.value = bucketsList.filter(b => b.commandeVenteId != emptyMongoId())

    buckets.value = bucketsList
      .filter(b => b.semiId != emptyMongoId() && b.commandeVenteId == emptyMongoId())

    newData.value = false
  } catch (e) {
    notifyError(e)
  } finally {
    isLoading.value = false
  }
}

function getBucketSemi(bucket: MDBVegcoBucket): MDBSemi | undefined {
  if (bucket.semiId == emptyMongoId()) return undefined
  return semis.value.find(s => s.id == bucket.semiId) ??
    semisVegco.value.find(s => s.id == bucket.semiId)
}

function getBucketChamp(bucket: MDBVegcoBucket): string {
  return getBucketSemi(bucket)?.champ ?? '?'
}


function getBucketsForDayAndCulture(day: Date, culture: string): MDBVegcoBucket[] {
  return buckets.value.filter(b => {
    const d = new Date(b.date)
    return d.getFullYear() == day.getFullYear() &&
      d.getMonth() == day.getMonth() &&
      d.getDate() == day.getDate() &&
      b.culture == culture
  })
}

function getTotalBoxes(day: Date, culture: string): number {
  let total = 0
  for (const b of getBucketsForDayAndCulture(day, culture)) {
    if (!b.semiFermé) total += b.quantité / unitésParBoîte(b.culture)
  }
  const cmdDay = commandeBuckets.value.filter(b => {
    const d = new Date(b.date)
    return d.getFullYear() == day.getFullYear() &&
      d.getMonth() == day.getMonth() &&
      d.getDate() == day.getDate() &&
      b.culture == culture
  })
  for (const b of cmdDay) {
    total += b.quantité / unitésParBoîte(b.culture)
  }
  return total
}

function getWeekTotal(culture: string): number {
  return weekDays.value.reduce((acc, d) => acc + getTotalBoxes(d, culture), 0)
}

function formatTotal(val: number): string {
  if (val === 0) return ''
  return Math.round(val).toString()
}

const confirmDialog = ref(false)
const pendingDrag = ref<{
  bucket: MDBVegcoBucket
  targetDay: Date
  siteCS: string
  isFloating: boolean
  floatingDayIdx?: number
  floatingDay?: { day: Date; isNextWeek: boolean; isPrevWeek: boolean }
} | null>(null)

async function onDrag(day: Date, args: any) {
  if (args['added'] == undefined) return
  const bucket = args.added.element as MDBVegcoBucket
  const siteCS = storeU.siteSélectionné.lettresDappel == 'VGCO' ? 'VGCO' :
    storeU.getSiteLettresDappel(bucket.siteId)
  pendingDrag.value = { bucket, targetDay: day, siteCS, isFloating: false }
  confirmDialog.value = true
}

async function confirmPendingDrag() {
  const pending = pendingDrag.value
  if (!pending) return
  confirmDialog.value = false
  try {
    pending.bucket.date = pending.targetDay.toISOString()
    pending.bucket.dateChangéeManuellement = true
    await apiProphet.put(`vgco/sites/${pending.siteCS}/buckets/${pending.bucket.id}`, pending.bucket)
    if (pending.isFloating && pending.floatingDay?.isNextWeek) nextWeek()
    if (pending.isFloating && pending.floatingDay?.isPrevWeek) previousWeek()
    Notify.success('Date de récolte prévue modifiée avec succès')
  } catch (e) {
    notifyError(e)
  } finally {
    if (pending.isFloating && pending.floatingDayIdx !== undefined) {
      floatingDropLists.value[pending.floatingDayIdx] = []
      isDragging.value = false
    }
    pendingDrag.value = null
    getData()
  }
}

function cancelPendingDrag() {
  confirmDialog.value = false
  if (pendingDrag.value?.isFloating && pendingDrag.value.floatingDayIdx !== undefined) {
    floatingDropLists.value[pendingDrag.value.floatingDayIdx] = []
    isDragging.value = false
  }
  pendingDrag.value = null
  getData()
}

const isDragging = ref(false)
const floatingDropLists = ref<MDBVegcoBucket[][]>(Array.from({ length: 9 }, () => []))
const hoveredFloatingIdx = ref<number | null>(null)

function onBackdropDragOver(e: DragEvent) {
  const el = (e.target as HTMLElement).closest('[data-floating-idx]')
  hoveredFloatingIdx.value = el ? parseInt(el.getAttribute('data-floating-idx')!) : null
}

watch(isDragging, (val) => { if (!val) hoveredFloatingIdx.value = null })

function previousWeek() {
  selectedWeek.value = addDays(weekStart.value, -7)
}
function nextWeek() {
  selectedWeek.value = addDays(weekStart.value, 7)
}

const theme = useTheme()
const display = useDisplay()

declare interface DayColumn {
  day: Date
  label: string
  sub: string
  isToday: boolean
}

const dayColumns = computed<DayColumn[]>(() =>
  weekDays.value.map(day => ({
    day,
    label: to_wd(day).substring(0, display.xs.value ? 3 : undefined),
    sub: to_dd_MM(day),
    isToday: (() => {
      const now = new Date()
      return day.getFullYear() == now.getFullYear() &&
        day.getMonth() == now.getMonth() &&
        day.getDate() == now.getDate()
    })()
  }))
)

const weekRow1 = computed(() => dayColumns.value.slice(0, 4))
const weekRow2 = computed(() => dayColumns.value.slice(4))

const floatingDays = computed(() => [
  ...dayColumns.value.map(col => ({ day: col.day, label: col.label, sub: col.sub, isNextWeek: false, isPrevWeek: false })),
  { day: addDays(weekStart.value, 7), label: '\u21aa Lun.', sub: to_dd_MM(addDays(weekStart.value, 7)), isNextWeek: true, isPrevWeek: false },
  { day: addDays(weekStart.value, -1), label: 'Dim. \u21ab', sub: to_dd_MM(addDays(weekStart.value, -1)), isNextWeek: false, isPrevWeek: true }
])

async function onDragFloating(dayIndex: number, args: any) {
  if (args['added'] == undefined) return
  const targetDay = floatingDays.value[dayIndex]
  const bucket = args.added.element as MDBVegcoBucket
  const siteCS = storeU.siteSélectionné.lettresDappel == 'VGCO' ? 'VGCO' :
    storeU.getSiteLettresDappel(bucket.siteId)
  pendingDrag.value = { bucket, targetDay: targetDay.day, siteCS, isFloating: true, floatingDayIdx: dayIndex, floatingDay: targetDay }
  confirmDialog.value = true
}

const dialogBucket = useTemplateRef('dialogBucket')
const dialogBuckets = useTemplateRef('dialogBuckets')

async function openBucket(bucket: MDBVegcoBucket, day: Date) {
  if (bucket.siteId != storeU.siteSélectionné.id) {
    const modifs = await dialogBucket.value?.openBucketEdit(bucket, day)
    if (modifs) getData()
  } else {
    const semi = semis.value.find(s => s.id == bucket.semiId)
    if (!semi) { notifyError('Erreur: le semi n\'existe pas.'); return }
    const modifs = await dialogBuckets.value?.openBucketsAsync(semi, bucket.id)
    if (modifs) {
      semis.value.splice(semis.value.findIndex(s => s.id == semi.id), 1)
      getData()
    }
  }
}

const dayListsForCulture = computed(() => {
  const result: Record<string, MDBVegcoBucket[][]> = {}
  for (const culture of cultures.value) {
    result[culture] = weekDays.value.map(day => getBucketsForDayAndCulture(day, culture))
  }
  return result
})

const intervalId = setInterval(async () => {
  if (await storeVegco.isRevisionChanged())
    newData.value = true
}, 5000)

onMounted(() => getData())
onUnmounted(() => { if (intervalId) clearInterval(intervalId) })
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" style="height: 85vh; display: flex; flex-direction: column;" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-4 pt-4 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 10px;">

        <!-- Left: icon + title -->
        <div class="d-flex align-center" style="gap: 10px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M9,10H7V17H9V10Z" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Planificateur</div>
            <div class="text-caption text-medium-emphasis">Glisser-déposer par jour de récolte</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn
            v-if="newData" color="warning" variant="tonal" rounded="pill" size="small"
            prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" @click="getData()">Actualiser</v-btn>
          <v-select
            v-model="selectedSite"
            :items="sites"
            label="Site"
            density="compact" variant="outlined" rounded="lg" hide-details
            style="max-width: 120px;"/>
        </div>
      </div>

      <!-- Week navigation -->
      <div class="d-flex align-center mt-3" style="gap: 8px;">
        <v-btn icon="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z" variant="text" density="compact" @click="previousWeek"/>
        <span class="text-body-2 font-weight-medium" style="min-width: 120px; text-align: center;">
          {{ to_dd_MMMM(weekDays[0]) }} – {{ to_dd_MMMM(weekDays[6]) }}
        </span>
        <v-btn icon="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" variant="text" density="compact" @click="nextWeek"/>
      </div>

      <!-- Culture chips -->
      <div v-if="cultures.length > 0" class="d-flex align-center mt-2" style="gap: 6px; flex-wrap: wrap;">
        <v-chip
          v-for="culture in cultures" :key="culture"
          size="small"
          :color="culture === selectedCulture ? 'primary' : undefined"
          :variant="culture === selectedCulture ? 'tonal' : 'outlined'"
          @click="selectedCulture = culture">
          {{ culture }}
        </v-chip>
      </div>
    </div>

    <v-divider/>

    <!-- Week grid -->
    <div style="flex: 1 1 auto; overflow-y: auto;" class="bigger-scrollbar">

      <!-- Row 1: Lundi – Jeudi -->
      <div class="week-half-section">
        <div class="week-header-row week-header-row--4">
          <div
            v-for="col in weekRow1" :key="col.sub"
            class="day-header-cell"
            :class="{ 'today-col': col.isToday }">
            <div class="text-caption font-weight-bold text-uppercase" style="line-height: 1.1;">
              {{ col.label }}
            </div>
            <div class="text-caption text-medium-emphasis">{{ col.sub }}</div>
            <div
              v-if="selectedCulture && formatTotal(getTotalBoxes(col.day, selectedCulture))"
              class="text-caption font-weight-bold total-chip">
              {{ formatTotal(getTotalBoxes(col.day, selectedCulture)) }}
            </div>
          </div>
        </div>
        <div v-if="selectedCulture" class="week-grid-row week-grid-row--4">
          <div
            v-for="(col, idx) in weekRow1" :key="col.sub"
            class="day-cell"
            :class="{ 'today-col': col.isToday }">
            <draggable
              class="drop-zone"
              group="buckets"
              :list="dayListsForCulture[selectedCulture]?.[idx] ?? []"
              item-key="id"
              :delay="200"
              :delay-on-touch-only="false"
              @start="isDragging = true"
              @end="isDragging = false"
              @change="onDrag(col.day, $event)">
              <template #item="{ element: bucket }">
                <div :key="bucket.id" class="bucket-chip">
                  <v-card
                    rounded="lg"
                    :color="getSemiVérifiéColor(getBucketSemi(bucket))"
                    variant="tonal"
                    class="bucket-inner"
                    @click="openBucket(bucket, col.day)">
                    <div class="bucket-number font-weight-bold">{{ bucket.numéroSemi }}</div>
                    <div v-if="selectedSite === 'Tous'" class="bucket-info text-caption">
                      {{ storeVegco.getSiteLettresDappelRenommé(bucket.siteId) }}
                    </div>
                    <v-chip v-else color="teal" size="x-small" variant="tonal" class="font-weight-bold mt-1" style="max-width:100%;">
                      {{ getBucketChamp(bucket) }}
                    </v-chip>
                    <div v-if="bucket.balises.length" class="d-flex flex-wrap" style="gap: 2px; margin-top: 2px;">
                      <v-chip
                        v-for="balise in bucket.balises" :key="balise"
                        size="x-small" variant="outlined" style="max-width:100%;">
                        {{ balise }}
                      </v-chip>
                    </div>
                    <div v-if="!bucket.semiFermé" class="bucket-qte text-caption">
                      {{ Math.round(bucket.quantité / unitésParBoîte(bucket.culture)) }} bx
                    </div>
                    <div v-else class="bucket-qte text-caption" style="color: red;">
                      <v-icon size="x-small" icon="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
                    </div>
                  </v-card>
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>

      <!-- Divider between the two rows -->
      <v-divider class="week-row-divider"/>

      <!-- Row 2: Vendredi – Dimanche -->
      <div class="week-half-section">
        <div class="week-header-row week-header-row--3">
          <div
            v-for="col in weekRow2" :key="col.sub"
            class="day-header-cell"
            :class="{ 'today-col': col.isToday }">
            <div class="text-caption font-weight-bold text-uppercase" style="line-height: 1.1;">
              {{ col.label }}
            </div>
            <div class="text-caption text-medium-emphasis">{{ col.sub }}</div>
            <div
              v-if="selectedCulture && formatTotal(getTotalBoxes(col.day, selectedCulture))"
              class="text-caption font-weight-bold total-chip">
              {{ formatTotal(getTotalBoxes(col.day, selectedCulture)) }}
            </div>
          </div>
        </div>
        <div v-if="selectedCulture" class="week-grid-row week-grid-row--3">
          <div
            v-for="(col, idx) in weekRow2" :key="col.sub"
            class="day-cell"
            :class="{ 'today-col': col.isToday }">
            <draggable
              class="drop-zone"
              group="buckets"
              :list="dayListsForCulture[selectedCulture]?.[idx + 4] ?? []"
              item-key="id"
              :delay="200"
              :delay-on-touch-only="false"
              @start="isDragging = true"
              @end="isDragging = false"
              @change="onDrag(col.day, $event)">
              <template #item="{ element: bucket }">
                <div :key="bucket.id" class="bucket-chip">
                  <v-card
                    rounded="lg"
                    :color="getSemiVérifiéColor(getBucketSemi(bucket))"
                    variant="tonal"
                    class="bucket-inner"
                    @click="openBucket(bucket, col.day)">
                    <div class="bucket-number font-weight-bold">{{ bucket.numéroSemi }}</div>
                    <div v-if="selectedSite === 'Tous'" class="bucket-info text-caption">
                      {{ storeVegco.getSiteLettresDappelRenommé(bucket.siteId) }}
                    </div>
                    <v-chip v-else color="teal" size="x-small" variant="tonal" class="font-weight-bold mt-1" style="max-width:100%;">
                      {{ getBucketChamp(bucket) }}
                    </v-chip>
                    <div v-if="bucket.balises.length" class="d-flex flex-wrap" style="gap: 2px; margin-top: 2px;">
                      <v-chip
                        v-for="balise in bucket.balises" :key="balise"
                        size="x-small" variant="outlined" style="max-width:100%;">
                        {{ balise }}
                      </v-chip>
                    </div>
                    <div v-if="!bucket.semiFermé" class="bucket-qte text-caption">
                      {{ Math.round(bucket.quantité / unitésParBoîte(bucket.culture)) }} bx
                    </div>
                    <div v-else class="bucket-qte text-caption" style="color: red;">
                      <v-icon size="x-small" icon="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
                    </div>
                  </v-card>
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>

      <!-- Weekly totals footer per culture -->
      <div v-if="selectedCulture" class="week-footer-row px-4 py-2 d-none d-sm-flex align-center">
        <span class="text-caption text-medium-emphasis">Total semaine {{ selectedCulture }} :</span>
        <span class="text-body-2 font-weight-bold ml-2">
          {{ formatTotal(getWeekTotal(selectedCulture)) || '0' }} boîtes
        </span>
      </div>

      <!-- Empty state -->
      <div
        v-if="!isLoading && selectedCulture && buckets.filter(b => b.culture == selectedCulture).length == 0"
        class="d-flex flex-column align-center justify-center py-8 text-medium-emphasis">
        <v-icon size="48" icon="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.11 3.9 21 5 21H19C20.11 21 21 20.11 21 19V5C21 3.9 20.11 3 19 3M19 19H5V9H19V19M19 7H5V5H19V7Z" class="mb-2"/>
        <span class="text-body-2">Aucun semi pour cette culture cette semaine</span>
      </div>
    </div>
  </v-card>

  <!-- Floating drop zones (visible while dragging) -->
  <Transition name="next-week-drop">
    <div v-if="isDragging" class="next-week-drop-backdrop" @dragover.prevent="onBackdropDragOver($event)">
      <div class="next-week-drop-target">
        <!-- Row 1: Dim préc. + Lun–Jeu -->
        <div class="floating-days-strip">
          <!-- Previous Sunday -->
          <div data-floating-idx="8">
          <draggable
            class="floating-day-zone floating-day-zone--prev-week"
            group="buckets"
            :list="floatingDropLists[8]"
            :disabled="hoveredFloatingIdx !== null && hoveredFloatingIdx !== 8"
            item-key="id"
            @change="onDragFloating(8, $event)">
            <template #header>
              <div class="floating-day-label">
                <div class="floating-day-name">{{ floatingDays[8].label }}</div>
                <div class="floating-day-sub">{{ floatingDays[8].sub }}</div>
              </div>
            </template>
            <template #item="{ element }"><span :key="element.id"/></template>
          </draggable>
          </div>
          <!-- Lun–Jeu (indices 0–3) -->
          <div v-for="(fd, idx) in floatingDays.slice(0, 4)" :key="fd.sub" :data-floating-idx="idx">
          <draggable
            class="floating-day-zone"
            group="buckets"
            :list="floatingDropLists[idx]"
            :disabled="hoveredFloatingIdx !== null && hoveredFloatingIdx !== idx"
            item-key="id"
            @change="onDragFloating(idx, $event)">
            <template #header>
              <div class="floating-day-label">
                <div class="floating-day-name">{{ fd.label }}</div>
                <div class="floating-day-sub">{{ fd.sub }}</div>
              </div>
            </template>
            <template #item="{ element }"><span :key="element.id"/></template>
          </draggable>
          </div>
        </div>
        <!-- Row 2: Ven–Dim + Lundi prochain -->
        <div class="floating-days-strip mt-2">
          <div v-for="(fd, idx) in floatingDays.slice(4, 8)" :key="fd.sub" :data-floating-idx="idx + 4">
          <draggable
            class="floating-day-zone"
            :class="{ 'floating-day-zone--next-week': fd.isNextWeek }"
            group="buckets"
            :list="floatingDropLists[idx + 4]"
            :disabled="hoveredFloatingIdx !== null && hoveredFloatingIdx !== idx + 4"
            item-key="id"
            @change="onDragFloating(idx + 4, $event)">
            <template #header>
              <div class="floating-day-label">
                <div class="floating-day-name">{{ fd.label }}</div>
                <div class="floating-day-sub">{{ fd.sub }}</div>
              </div>
            </template>
            <template #item="{ element }"><span :key="element.id"/></template>
          </draggable>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <DialogBucket ref="dialogBucket"/>
  <DialogBuckets ref="dialogBuckets"/>

  <v-dialog v-model="confirmDialog" max-width="420" persistent>
    <v-card rounded="xl">
      <v-card-title class="pt-5 px-5">
        <v-icon icon="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H10V19H5V8H19V9H21V5A2,2 0 0,0 19,3M21.7,13.35L20.7,14.35L18.65,12.35L19.65,11.35C19.85,11.14 20.19,11.13 20.42,11.35L21.7,12.63C21.89,12.83 21.89,13.15 21.7,13.35M12,18.94L18.07,12.88L20.12,14.88L14.06,21H12V18.94Z" class="mr-2" color="primary"/>
        Déplacer le bucket
      </v-card-title>
      <v-card-text class="px-5">
        Déplacer le semi
        <strong>{{ pendingDrag?.bucket.numéroSemi }}</strong>
        au
        <strong>{{ pendingDrag ? to_dd_MMMM(pendingDrag.targetDay) : '' }}</strong>
        ?
      </v-card-text>
      <v-card-actions class="pb-4 px-5">
        <v-spacer/>
        <v-btn variant="text" @click="cancelPendingDrag()">Annuler</v-btn>
        <v-btn color="primary" variant="tonal" rounded="pill" @click="confirmPendingDrag()">Confirmer</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Week grid ──────────────────────────── */
.week-half-section {
  border-bottom: none;
}

.week-row-divider {
  border-width: 2px;
  opacity: 0.2;
  margin: 0;
}

.week-header-row {
  display: grid;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: v-bind('theme.global.name.value === "dark" ? "rgb(var(--v-theme-surface))" : "#fff"');
}

.week-header-row--4 {
  grid-template-columns: repeat(4, 1fr);
}

.week-header-row--3 {
  grid-template-columns: repeat(3, 1fr);
}

.day-header-cell {
  padding: 6px 4px 4px;
  text-align: center;
  border-right: 1px solid rgba(var(--v-border-color), 0.15);
  position: relative;
}

.day-header-cell:last-child {
  border-right: none;
}

.today-col {
  background: rgba(var(--v-theme-primary), 0.08);
}

.total-chip {
  display: inline-block;
  margin-top: 2px;
  background: rgba(var(--v-theme-primary), 0.15);
  color: rgb(var(--v-theme-primary));
  border-radius: 8px;
  padding: 0 6px;
  font-size: 10px;
}

.week-grid-row {
  display: grid;
  align-items: start;
  min-height: 120px;
}

.week-grid-row--4 {
  grid-template-columns: repeat(4, 1fr);
}

.week-grid-row--3 {
  grid-template-columns: repeat(3, 1fr);
}

.day-cell {
  padding: 4px 2px;
  border-right: 1px solid rgba(var(--v-border-color), 0.15);
  min-height: 120px;
}

.day-cell:last-child {
  border-right: none;
}

.drop-zone {
  min-height: 110px;
  height: 100%;
  padding: 2px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 8px;
  transition: background 0.15s;
}

.drop-zone:empty {
  border: 1px dashed rgba(var(--v-border-color), 0.3);
  border-radius: 8px;
}

.bucket-chip {
  touch-action: none;
  user-select: none;
}

.bucket-inner {
  padding: 4px 6px;
  cursor: grab;
  min-width: 0;
  width: 100%;
  overflow: hidden;
}

.bucket-inner:active {
  cursor: grabbing;
}

.bucket-number {
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bucket-info {
  font-size: 10px;
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bucket-qte {
  font-size: 10px;
  opacity: 0.7;
}

.week-footer-row {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-surface-variant), 0.12);
}

.bigger-scrollbar {
  scrollbar-width: auto;
}

/* ── Floating drop targets (during drag) ──── */
.next-week-drop-backdrop {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding-bottom: 56px; /* above app footer */
  padding-top: 16px;
  background: rgba(var(--v-theme-surface), 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.next-week-drop-target {
  width: min(700px, 95vw);
}

.floating-days-strip {
  display: flex;
  gap: 6px;
}

.floating-days-strip > div {
  flex: 1;
  display: flex;
}

.floating-day-zone {
  flex: 1;
  background: rgba(var(--v-theme-surface), 0.92);
  border: 2px dashed rgba(var(--v-border-color), 0.5);
  border-radius: 10px;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.floating-day-zone--next-week {
  background: rgba(var(--v-theme-primary), 0.15);
  border-color: rgb(var(--v-theme-primary));
}

.floating-day-zone--prev-week {
  background: rgba(var(--v-theme-secondary), 0.15);
  border-color: rgb(var(--v-theme-secondary));
}

.floating-day-zone--prev-week .floating-day-name,
.floating-day-zone--prev-week .floating-day-sub {
  color: rgb(var(--v-theme-secondary));
}

.floating-day-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 4px;
  pointer-events: none;
  line-height: 1.3;
}

.floating-day-name {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.floating-day-sub {
  font-size: 10px;
  opacity: 0.7;
}

.floating-day-zone--next-week .floating-day-name,
.floating-day-zone--next-week .floating-day-sub {
  color: rgb(var(--v-theme-primary));
}

.next-week-drop-enter-active,
.next-week-drop-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.next-week-drop-enter-from,
.next-week-drop-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
